import React, { createContext, useContext, useEffect, useState } from 'react';
import pwaService from '../services/pwaService';
import toast from 'react-hot-toast';

const PWAContext = createContext();

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export const PWAProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [pwaSupported, setPwaSupported] = useState(false);

  useEffect(() => {
    // Register service worker
    const initPWA = async () => {
      const supported = await pwaService.register();
      setPwaSupported(supported);

      if (supported) {
        // Request notification permission
        await pwaService.requestNotificationPermission();
        
        // Push notifications disabled for now - no valid VAPID key
        // await pwaService.subscribeToPushNotifications();
      }
    };

    initPWA();

    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('You are back online!');
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error('You are offline. Some features may be limited.');
    };

    pwaService.addOnlineListener(handleOnline);
    pwaService.addOfflineListener(handleOffline);

    // Check if app is installed
    const checkInstallation = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isInStandaloneMode = ('standalone' in window.navigator) && window.navigator.standalone;
      
      setIsInstalled(isStandalone || (isIOS && isInStandaloneMode));
    };

    checkInstallation();

    // Keep installation state in sync (e.g., after install without full refresh)
    const media = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = () => checkInstallation();
    if (media?.addEventListener) {
      media.addEventListener('change', handleDisplayModeChange);
    } else if (media?.addListener) {
      // Safari / older browsers
      media.addListener(handleDisplayModeChange);
    }

    // Listen for beforeinstallprompt: preventDefault() so we can show our own install banner.
    // We call prompt() only when the user clicks "Install" (pwaService.showInstallPrompt()).
    // If the user dismisses our banner without installing, Chrome may log a one-time warning;
    // that is expected and harmless.
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      toast.success('App installed successfully!');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (media?.removeEventListener) {
        media.removeEventListener('change', handleDisplayModeChange);
      } else if (media?.removeListener) {
        media.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  const handleInstall = () => {
    pwaService.showInstallPrompt();
    setShowInstallPrompt(false);
  };

  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
  };

  const value = {
    isOnline,
    isInstalled,
    showInstallPrompt,
    pwaSupported,
    handleInstall,
    dismissInstallPrompt,
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
      {/* Install Prompt */}
      {showInstallPrompt && !isInstalled && (
        <div className="fixed bottom-4 left-4 right-4 bg-blue-600 text-white p-4 rounded-lg shadow-lg z-50 md:left-auto md:right-4 md:max-w-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Install SaleSign</h3>
              <p className="text-sm opacity-90">Get quick access to property search</p>
            </div>
            <div className="flex space-x-2 ml-4">
              <button
                onClick={handleInstall}
                className="bg-white text-blue-600 px-3 py-1 rounded text-sm font-medium hover:bg-gray-100"
              >
                Install
              </button>
              <button
                onClick={dismissInstallPrompt}
                className="text-white hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Offline Indicator */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white text-center py-2 z-50">
          <span className="text-sm">📡 You are offline. Some features may be limited.</span>
        </div>
      )}
    </PWAContext.Provider>
  );
};
